import { useState } from 'react';
import { PHASE_OPTIONS } from '../../ui/halftime/phaseOptions';
import type { HypothesisPhase } from '../../types';
import { PhaseIcon } from '../PhaseIcon';
import { SheetB } from '../SheetB';

/**
 * B spec §2.4 setup section 5 — **the create-custom-pattern sheet** (build unit B-7, PRE-3):
 * free text plus the seven-phase picker. Typing is legal here — setup is an out-of-match
 * surface (PRE-3 permits free text at Pre-Match and Halftime, and nowhere else).
 *
 * `PHASE_OPTIONS` is imported from its one home (`src/ui/halftime/phaseOptions.ts`) rather
 * than copied: it is a pure vocabulary module with no A-design coupling, its compile-time
 * exhaustiveness guard ("every `HypothesisPhase` is offered") is the thing a copy would lose,
 * and a ui-b sibling list is exactly what the reuse contract's duplication gate exists to
 * flag. Importing does not alter A (clause 4: A's files are untouched).
 *
 * The phase is a required choice with **no default**, like the two gates on the screen behind
 * it — a picker that opened on "In possession" would file half the user's patterns on the
 * wrong Discovery page silently.
 */

export interface AddPatternSheetProps {
  readonly onAdd: (text: string, phase: HypothesisPhase) => void;
  readonly onClose: () => void;
}

export function AddPatternSheet({ onAdd, onClose }: AddPatternSheetProps) {
  const [text, setText] = useState('');
  const [phase, setPhase] = useState<HypothesisPhase | null>(null);
  const incomplete = text.trim() === '' || phase === null;

  return (
    <SheetB
      titleId="setup-pattern-title"
      title="Add your own pattern"
      testId="setup-pattern-sheet"
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="sheet-b__primary"
            data-testid="setup-pattern-save"
            disabled={incomplete}
            onClick={() => {
              if (phase === null) return;
              onAdd(text, phase);
            }}
          >
            Add pattern
          </button>
          <button
            type="button"
            className="sheet-b__secondary"
            data-testid="setup-pattern-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </>
      }
    >
      <label className="sheet-b__label" htmlFor="setup-pattern-text">
        What to watch for
      </label>
      <textarea
        id="setup-pattern-text"
        className="sheet-b__field sheet-b__field--area"
        data-testid="setup-pattern-text"
        rows={3}
        placeholder="“Their #9 drifts wide when they build up”"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />

      <p className="sheet-b__label" id="setup-pattern-phase-label">
        Game phase
      </p>
      <div
        className="setup-phase-picker"
        role="radiogroup"
        aria-labelledby="setup-pattern-phase-label"
      >
        {PHASE_OPTIONS.map((option) => (
          <button
            key={option.phase}
            type="button"
            role="radio"
            aria-checked={phase === option.phase}
            className="setup-phase-option"
            data-testid={`setup-pattern-phase-${option.phase}`}
            onClick={() => {
              setPhase(option.phase);
            }}
          >
            <PhaseIcon phase={option.phase} />
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </SheetB>
  );
}
